import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "../../../../lib/network/api";

// ==================== INTERFACES ====================

export type ApplicationStatus =
  | "pending"
  | "reviewed"
  | "shortlisted"
  | "rejected"
  | "hired";

export interface Application {
  _id: string;
  applicationId: string;
  jobId: string;
  jobTitle: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  location: string;
  experience: string;
  signupCode: string;
  availability: string;
  coverLetter?: string;
  resumeUrl?: string;
  status: ApplicationStatus;
  adminNotes?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  createdAt: string;
  updatedAt: string;
  geoIp?: string;
  geoCity?: string;
  geoRegion?: string;
  geoCountry?: string;
  geoCountryCode?: string;
  geoTimezone?: string;
  geoLatitude?: string;
  geoLongitude?: string;
}

export interface ApplicationsResponse {
  applications: Application[];
  total: number;
  currentPage: number;
  totalPages: number;
}

export interface ApplicationsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  jobId?: string;
}

export interface UpdateApplicationPayload {
  status?: ApplicationStatus;
  adminNotes?: string;
}

// ==================== API FUNCTIONS ====================

export const getAllApplications = async (
  params?: ApplicationsParams
): Promise<Application[]> => {
  const response: any = await api.get("/applications", {
    ...params,
  });
  return response.data?.data || response.data;
};

export const getApplicationById = async (id: string): Promise<Application> => {
  const response: any = await api.get(`/applications/${id}`, {
    headers: { "Cache-Control": "no-cache" },
  });
  return response.data?.data || response.data;
};

export const updateApplication = async (
  id: string,
  payload: UpdateApplicationPayload
): Promise<Application> => {
  const response: any = await api.patch(`/applications/${id}`, payload);
  return response.data?.data || response.data;
};

// ==================== HOOKS ====================

export const useGetAllApplications = (params?: ApplicationsParams) => {
  return useQuery({
    queryKey: ["admin-applications", params],
    queryFn: () => getAllApplications(params),
    retry: 2,
    refetchOnWindowFocus: false,
  });
};

export const useGetApplicationById = (id: string) => {
  return useQuery({
    queryKey: ["admin-application", id],
    queryFn: () => getApplicationById(id),
    enabled: !!id,
    retry: 2,
    refetchOnWindowFocus: false,
  });
};

export const useUpdateApplication = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateApplicationPayload;
    }) => updateApplication(id, payload),
    onSuccess: (_, { id }) => {
      toast.success("Application updated successfully");
      queryClient.invalidateQueries({ queryKey: ["admin-applications"] });
      queryClient.invalidateQueries({ queryKey: ["admin-application", id] });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || "Failed to update application";
      toast.error(message);
    },
  });
};
